/**
 * System states (HANDOFF screen 37): each says what happened and what to do; deadlines keep running.
 * One h1 per state, focused on mount (HANDOFF §0.6: after a route change the page's h1 takes focus).
 */
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Clock, KeyRound, LogIn, RotateCw, ShieldOff, Trash2, Wrench, type LucideIcon } from "lucide-react";
import { Button, DialogContent, DialogRoot } from "@brevalt/ui";
import { rethrowLater, usePending } from "../pending";

function StateLayout({
  icon: Icon,
  title,
  children,
  actions,
}: {
  icon: LucideIcon;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const h1 = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    h1.current?.focus();
  }, []);
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center">
      <span className="grid size-12 place-items-center rounded-full bg-surface-2 text-text-muted">
        <Icon aria-hidden className="size-6" />
      </span>
      <h1 ref={h1} tabIndex={-1} className="mt-4 text-xl font-semibold outline-none">
        {title}
      </h1>
      <div className="mt-2 space-y-2 text-sm text-text-muted">{children}</div>
      {actions && <div className="mt-6 flex flex-wrap justify-center gap-2">{actions}</div>}
    </div>
  );
}

/** 500: our side failed. Shows the request id as the reference for support; "Try again" spins while a refetch runs. */
export function ServerErrorState({
  requestId,
  onRetry,
  statusPageUrl,
}: {
  requestId?: string;
  onRetry?: () => unknown;
  statusPageUrl?: string;
}) {
  const { t } = useTranslation();
  const pending = usePending();
  return (
    <StateLayout
      icon={Wrench}
      title={t("misc.something_went_wrong_on_our_side")}
      actions={
        <>
          {onRetry && (
            <Button
              variant="primary"
              icon={RotateCw}
              loading={pending.busy("retry")}
              onClick={() => {
                pending.run("retry", () => Promise.resolve(onRetry())).catch(rethrowLater);
              }}
            >
              {t("misc.try_again")}
            </Button>
          )}
          {statusPageUrl && (
            <Button variant="ghost" asChild>
              <a href={statusPageUrl} rel="noopener noreferrer" target="_blank">
                {t("misc.status_page")}
              </a>
            </Button>
          )}
        </>
      }
    >
      <p>{t("misc.your_data_is_safe_deadlines_alerts")}</p>
      {requestId && (
        <p>
          {t("misc.if_it_keeps_happening_send_us")}{" "}
          <span className="font-mono text-text" data-testid="request-id">
            {requestId}
          </span>
        </p>
      )}
    </StateLayout>
  );
}

/** 404: an unknown route, an unsafe path (REQUESTS #6) or an id outside the member's product scope. */
export function NotFoundState({ homeHref = "/" }: { homeHref?: string }) {
  const { t } = useTranslation();
  return (
    <StateLayout
      icon={LogIn}
      title={t("misc.we_can_t_find_this_page")}
      actions={
        <Button variant="primary" asChild>
          <a href={homeHref}>{t("misc.go_to_the_dashboard")}</a>
        </Button>
      }
    />
  );
}

/** Maintenance (503 from the proxy while the API is updated). */
export function MaintenanceState({ statusPageUrl }: { statusPageUrl?: string }) {
  const { t } = useTranslation();
  return (
    <StateLayout
      icon={Clock}
      title={t("misc.brevalt_is_being_updated")}
      actions={
        statusPageUrl && (
          <Button variant="ghost" asChild>
            <a href={statusPageUrl} rel="noopener noreferrer" target="_blank">
              {t("misc.status_page")}
            </a>
          </Button>
        )
      }
    >
      <p>{t("misc.deadline_alerts_and_escalations_still_go")}</p>
    </StateLayout>
  );
}

/** The session ended: a modal without a close button; signing in again is the only way on (drafts are saved). */
export function SessionEndedDialog({ open, onSignIn }: { open: boolean; onSignIn: () => void }) {
  const { t } = useTranslation();
  return (
    <DialogRoot open={open}>
      <DialogContent
        hideClose
        title={t("misc.your_session_ended")}
        description={t("misc.drafts_save_as_you_type")}
        footer={
          <Button variant="primary" icon={KeyRound} onClick={onSignIn}>
            {t("misc.sign_in_again")}
          </Button>
        }
      >
        <p className="text-xs text-text-muted">{t("misc.deadline_alerts_keep_running_while_you")}</p>
      </DialogContent>
    </DialogRoot>
  );
}

/** No permission (screen 37 `no-permission`): which page, the member's role, who can change it. */
export function NoPermissionState({ page, role, homeHref }: { page: string; role?: string; homeHref?: string }) {
  const { t } = useTranslation();
  return (
    <StateLayout
      icon={ShieldOff}
      title={t("shell.you_can_t_open_page", { page })}
      actions={
        homeHref && (
          <Button variant="primary" asChild>
            <a href={homeHref}>{t("misc.go_to_the_dashboard")}</a>
          </Button>
        )
      }
    >
      {role && <p>{t("shell.no_permission_body", { role })}</p>}
    </StateLayout>
  );
}

/** Signed in, but no workspace (`GET /me` → `currentTenant: null`, `tenantProblem`). */
export function NoWorkspaceState() {
  const { t } = useTranslation();
  return (
    <StateLayout icon={Building2} title={t("shell.no_workspace_title")}>
      <p>{t("shell.no_workspace_body")}</p>
    </StateLayout>
  );
}

/** The workspace is being deleted (`status: deleting`, HANDOFF §0.16): no access any more; this page only. */
export function WorkspaceDeletingState() {
  const { t } = useTranslation();
  return (
    <StateLayout icon={Trash2} title={t("shell.workspace_being_deleted")}>
      <p>{t("shell.workspace_being_deleted_body")}</p>
    </StateLayout>
  );
}
